import React from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { Line, Bar, Pie, Doughnut } from 'react-chartjs-2';
import { Card, CardContent, CardHeader, CardTitle } from './card';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

/**
 * Default Chart.js options with brand styling
 */
const defaultOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      position: 'top',
      labels: {
        font: {
          family: 'Urbanist, sans-serif',
          size: 12
        },
        color: '#313131', // brand-dark
        padding: 15
      }
    },
    tooltip: {
      backgroundColor: '#313131',
      titleColor: '#ffffff',
      bodyColor: '#ffffff',
      borderColor: '#f19f57',
      borderWidth: 1,
      padding: 12,
      boxPadding: 6,
      usePointStyle: true,
      font: {
        family: 'Urbanist, sans-serif',
        size: 12
      }
    }
  },
  scales: {
    x: {
      grid: {
        display: false
      },
      ticks: {
        color: '#313131',
        font: {
          family: 'Urbanist, sans-serif',
          size: 11
        }
      }
    },
    y: {
      grid: {
        color: '#f9f9f9',
        borderDash: [5, 5]
      },
      ticks: {
        color: '#313131',
        font: {
          family: 'Urbanist, sans-serif',
          size: 11
        }
      }
    }
  }
};

/**
 * Brand color palette
 */
export const brandColors = {
  primary: '#f19f57',
  dark: '#313131',
  light1: '#f9f9f9',
  light2: '#f9f9f9',
  success: '#10b981',
  warning: '#f59e0b',
  danger: '#ef4444',
  info: '#3b82f6',
  purple: '#8b5cf6',
  pink: '#ec4899',
  teal: '#14b8a6',
  orange: '#f97316'
};

/**
 * Generate color palette for multiple datasets
 */
export const generateColorPalette = (count) => {
  const colors = Object.values(brandColors);
  return Array.from({ length: count }, (_, i) => colors[i % colors.length]);
};

/**
 * Line Chart Wrapper
 */
export function LineChart({ data, options = {}, title, height = 300, className = '' }) {
  const mergedOptions = {
    ...defaultOptions,
    ...options,
    plugins: {
      ...defaultOptions.plugins,
      ...options.plugins
    }
  };

  return (
    <Card className={className}>
      {title && (
        <CardHeader>
          <CardTitle className="text-brand-dark">{title}</CardTitle>
        </CardHeader>
      )}
      <CardContent>
        <div style={{ height }}>
          <Line data={data} options={mergedOptions} />
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * Bar Chart Wrapper
 */
export function BarChart({ data, options = {}, title, height = 300, horizontal = false, className = '' }) {
  const mergedOptions = {
    ...defaultOptions,
    indexAxis: horizontal ? 'y' : 'x',
    ...options,
    plugins: {
      ...defaultOptions.plugins,
      ...options.plugins
    }
  };

  return (
    <Card className={className}>
      {title && (
        <CardHeader>
          <CardTitle className="text-brand-dark">{title}</CardTitle>
        </CardHeader>
      )}
      <CardContent>
        <div style={{ height }}>
          <Bar data={data} options={mergedOptions} />
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * Pie Chart Wrapper
 */
export function PieChart({ data, options = {}, title, height = 300, className = '' }) {
  const mergedOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      ...defaultOptions.plugins,
      ...options.plugins,
      legend: {
        ...defaultOptions.plugins.legend,
        position: 'right'
      }
    }
  };

  return (
    <Card className={className}>
      {title && (
        <CardHeader>
          <CardTitle className="text-brand-dark">{title}</CardTitle>
        </CardHeader>
      )}
      <CardContent>
        <div style={{ height }}>
          <Pie data={data} options={mergedOptions} />
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * Doughnut Chart Wrapper
 */
export function DoughnutChart({ data, options = {}, title, height = 300, className = '' }) {
  const mergedOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      ...defaultOptions.plugins,
      ...options.plugins,
      legend: {
        ...defaultOptions.plugins.legend,
        position: 'right'
      }
    },
    cutout: '60%'
  };

  return (
    <Card className={className}>
      {title && (
        <CardHeader>
          <CardTitle className="text-brand-dark">{title}</CardTitle>
        </CardHeader>
      )}
      <CardContent>
        <div style={{ height }}>
          <Doughnut data={data} options={mergedOptions} />
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * Multi-line Chart (for comparisons)
 */
export function MultiLineChart({ datasets, labels, options = {}, title, height = 300, className = '' }) {
  const colors = generateColorPalette(datasets.length);
  
  const data = {
    labels,
    datasets: datasets.map((dataset, index) => ({
      ...dataset,
      borderColor: dataset.borderColor || colors[index],
      backgroundColor: dataset.backgroundColor || `${colors[index]}33`, // 20% opacity
      tension: 0.4,
      fill: dataset.fill !== undefined ? dataset.fill : false
    }))
  };

  return <LineChart data={data} options={options} title={title} height={height} className={className} />;
}

/**
 * Stacked Bar Chart
 */
export function StackedBarChart({ datasets, labels, options = {}, title, height = 300, horizontal = false, className = '' }) {
  const colors = generateColorPalette(datasets.length);
  
  const data = {
    labels,
    datasets: datasets.map((dataset, index) => ({
      ...dataset,
      backgroundColor: dataset.backgroundColor || colors[index],
      borderColor: dataset.borderColor || colors[index],
      borderWidth: 1
    }))
  };

  const mergedOptions = {
    ...options,
    scales: {
      ...options.scales,
      x: {
        ...options.scales?.x,
        stacked: true
      },
      y: {
        ...options.scales?.y,
        stacked: true
      }
    }
  };

  return <BarChart data={data} options={mergedOptions} title={title} height={height} horizontal={horizontal} className={className} />;
}

/**
 * Simple metric display (for single values)
 */
export function MetricCard({ label, value, change, changeType = 'neutral', icon: Icon, className = '' }) {
  const changeColor = changeType === 'positive' ? 'text-green-600' : 
                     changeType === 'negative' ? 'text-red-600' : 
                     'text-brand-dark/70';

  return (
    <Card className={className}>
      <CardContent className="pt-6">
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <p className="text-sm font-medium text-brand-dark/70">{label}</p>
            <p className="text-2xl font-bold text-brand-dark mt-1">{value}</p>
            {change && (
              <p className={`text-sm mt-1 ${changeColor}`}>
                {change}
              </p>
            )}
          </div>
          {Icon && (
            <div className="ml-4">
              <Icon className="h-8 w-8 text-brand-accent" />
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export default {
  LineChart,
  BarChart,
  PieChart,
  DoughnutChart,
  MultiLineChart,
  StackedBarChart,
  MetricCard,
  brandColors,
  generateColorPalette
};
